import {
  GetAllRolesResponse,
  getAllRolesService,
} from "@/service/auth.service";
import {
  createUserService,
  getAllUsersService,
  getUserDetailService,
  getUserFilterListService,
  updateUserService,
} from "@/service/user.service";
import { User, UserFilterListResponse, UserQuery } from "@/types/user.model";
import { capitalizeFirstLetter } from "@/utils/constants";
import { successToast } from "@/utils/toast";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import { updateDisplayName } from "./authSlice";

interface UserState {
  users: User[];
  filteredUsers: User[];
  searchTerm: string;
  roleFilter: string;
  statusFilter: string;
  loading: boolean;
  noUsers: boolean;
  error: string | null;
  roleOptions: GetAllRolesResponse[];
  userOptions: UserFilterListResponse[];
  loadingRoleOptions: boolean;
  loadingUserOptions: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

const initialState: UserState = {
  users: [],
  filteredUsers: [],
  searchTerm: "",
  roleFilter: "All roles",
  statusFilter: "All statuses",
  loading: false,
  noUsers: false,
  error: null,
  roleOptions: [],
  userOptions: [],
  loadingRoleOptions: false,
  loadingUserOptions: false,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 20,
  },
};

export const fetchRoleOptions = createAsyncThunk(
  "users/fetchRoleOptions",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getAllRolesService();
      return data;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to fetch role options";
      return rejectWithValue(message);
    }
  }
);

export const fetchUserOptions = createAsyncThunk(
  "users/fetchUserOptions",
  async ({ showAll }: { showAll: boolean }, { rejectWithValue }) => {
    try {
      const { data } = await getUserFilterListService({ showAll });
      return data;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to fetch user options";
      return rejectWithValue(message);
    }
  }
);

export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (
    query: UserQuery = { page: 1, pageSize: 20 },
    { dispatch, getState }
  ) => {
    try {
      dispatch(setLoading(true));

      // Get current filters from state if not provided in query
      const state = getState() as RootState;
      const currentQuery: UserQuery = {
        page: query.page || state.users.pagination.currentPage,
        pageSize: query.pageSize || state.users.pagination.pageSize,
        search:
          query.search !== undefined
            ? query.search
            : state.users.searchTerm || undefined,
        role:
          state.users.roleFilter !== "All roles"
            ? state.users.roleFilter
            : undefined,
        status:
          state.users.statusFilter !== "All statuses"
            ? state.users.statusFilter.toLowerCase()
            : undefined,
      };

      const {
        data: { data, pagination },
      } = await getAllUsersService(currentQuery);

      dispatch(setNoUsers(false));

      return {
        users: data,
        pagination: {
          currentPage: pagination?.current_page as number,
          totalPages: pagination?.total_pages as number,
          totalItems: pagination?.total_items as number,
          pageSize: pagination?.page_size as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoUsers(true));
        throw error;
      }

      const message = error.response?.data?.message || "Failed to fetch users";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const getUserDetail = createAsyncThunk(
  "users/getUserDetail",
  async (userId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const { data } = await getUserDetailService(userId);
      return data.data;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to fetch user details";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addUser = createAsyncThunk(
  "users/addUser",
  async (userData: User, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

     const data = await createUserService(userData);
      successToast(`User added successfully`, "");

      const state = getState() as RootState;
      dispatch(
        fetchUsers({
          page: state.users.pagination.currentPage,
          pageSize: state.users.pagination.pageSize,
        })
      );

      return data.data.data;
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to add user";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Update user thunk
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async (userData: User, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));
      await updateUserService(userData.id!, userData);

      const state = getState() as RootState;

      if (userData?.id === state.auth.user?.id) {
        dispatch(
          updateDisplayName({
            first_name: userData.first_name as string,
            last_name: userData.last_name as string,
          })
        );
      }

      successToast(
        `${capitalizeFirstLetter(
          userData.role?.name as string
        )} updated successfully`,
        ""
      );

      dispatch(
        fetchUsers({
          page: state.users.pagination.currentPage,
          pageSize: state.users.pagination.pageSize,
        })
      );

      return userData;
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to update user";
      dispatch(setError(message));
      throw error;
    }
  }
);

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setNoUsers: (state, action: PayloadAction<boolean>) => {
      state.noUsers = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setRoleFilter: (state, action: PayloadAction<string>) => {
      state.roleFilter = action.payload;
    },
    setStatusFilter: (state, action: PayloadAction<string>) => {
      state.statusFilter = action.payload;
    },
    resetFilters: (state) => {
      state.searchTerm = "";
      state.roleFilter = "All roles";
      state.statusFilter = "All statuses";
    },
    resetAllState: () => initialState,
    // No longer need applyFilters since we're using API filtering
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users = action.payload.users;
        state.filteredUsers = action.payload.users;
        state.pagination = action.payload.pagination;
        state.loading = false;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch users";
      })
      .addCase(fetchUserOptions.pending, (state) => {
        state.loadingUserOptions = true;
        state.error = null;
      })
      .addCase(fetchUserOptions.fulfilled, (state, action) => {
        state.userOptions = action.payload;
        state.loadingUserOptions = false;
      })
      .addCase(fetchUserOptions.rejected, (state) => {
        state.loadingUserOptions = false;
      })
      .addCase(fetchRoleOptions.pending, (state) => {
        state.loadingRoleOptions = true;
        state.error = null;
      })
      .addCase(fetchRoleOptions.fulfilled, (state, action) => {
        state.roleOptions = action.payload;
        state.loadingRoleOptions = false;
      })
      .addCase(fetchRoleOptions.rejected, (state, action) => {
        state.loadingRoleOptions = false;
      })
      .addCase(getUserDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserDetail.fulfilled, (state) => {
        state.loading = false;
        // We don't need to update the users list here as we're just getting details for the form
      })
      .addCase(getUserDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch user details";
      })
      .addCase(addUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addUser.fulfilled, (state) => {
        // We don't need to update the state manually here since fetchUsers will be called
        state.loading = false;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to add user";
      })
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state) => {
        // We don't need to update the state manually here since fetchUsers will be called
        state.loading = false;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update user";
      });
  },
});

// Client-side filtering removed as per requirements

export const {
  setLoading,
  setError,
  setSearchTerm,
  setRoleFilter,
  setStatusFilter,
  resetFilters,
  resetAllState,
  setNoUsers,
} = userSlice.actions;

// Additional selectors for pagination
export const selectPagination = (state: RootState) => state.users.pagination;
export const selectRoleOptions = (state: RootState) => state.users.roleOptions;
export const selectLoadingRoleOptions = (state: RootState) =>
  state.users.loadingRoleOptions;

// Selectors
export const selectUsers = (state: RootState) => state.users.users;
export const selectFilteredUsers = (state: RootState) =>
  state.users.filteredUsers;
export const selectSearchTerm = (state: RootState) => state.users.searchTerm;
export const selectRoleFilter = (state: RootState) => state.users.roleFilter;
export const selectStatusFilter = (state: RootState) =>
  state.users.statusFilter;
export const selectUserLoading = (state: RootState) => state.users.loading;
export const selectUserError = (state: RootState) => state.users.error;
export const selectUserOptions = (state: RootState) => state.users.userOptions;
export const selectLoadingUserOptions = (state: RootState) =>
  state.users.loadingUserOptions;
export const selectNoUsers = (state: RootState) => state.users.noUsers;

export default userSlice.reducer;
